import { Chart, ChartData, ChartOptions } from 'chart.js'
import { Bar, getElementAtEvent } from 'react-chartjs-2'
import ChartsHelper from './ChartsHelper'
import _ from 'lodash'
import { useRef } from 'react'
import Helper from '../../helpers/Helper'

interface Props {
    data: ChartData<'bar', number[], unknown>
    options?: ChartOptions<'bar'>
    isLoading?: boolean
    onClickSerie?: (options: { seriesName: string, index: number }) => void
}

const BarChart = ({ data, options, isLoading, onClickSerie }: Props) => {
    let chartData = data
    let newOptions = _.cloneDeep(options) ?? _.cloneDeep(BarChartDefaultOptions)

    const chartRef = useRef<Chart<'bar', number[], unknown>>(null)

    const handleChartHover = (event: any, activeElements: Array<any>) => {
        const canvas = chartRef.current?.canvas

        if (activeElements.length > 0 && Helper.hasVal(onClickSerie)) {
            canvas.style.cursor = 'pointer'
        } else {
            canvas.style.cursor = 'default'
        }
    }

    const handleChartClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
        const myChart = chartRef.current
        const elements = getElementAtEvent(myChart, event)

        if (Helper.hasVal(elements) && elements.length > 0) {
            const datasetIndex = elements[0].datasetIndex
            const dataIndex = elements[0].index

            if (onClickSerie)
                onClickSerie({ index: dataIndex, seriesName: data.datasets[datasetIndex].label })
        }
    }

    if (data.datasets.some(x => x.yAxisID === 'y2')) {
        newOptions.scales['y2'] = {
            grid: { display: false },
            beginAtZero: true,
            type: 'linear',
            axis: 'y',
            position: 'right',
            ticks: {
                font: { size: 11 },
                callback(tickValue) {
                    const formatter = Intl.NumberFormat('en', { notation: 'compact' })
                    return formatter.format(tickValue as number)
                }
            }
        }
    }

    if (isLoading) {
        chartData = {
            datasets: [
                { data: Array(12).fill(0)?.map(() => Math.random() * 10), order: 1 }
            ],
            labels: Array(12).fill(0).map(() => '')
        }

        if (data.datasets.some(x => x.yAxisID === 'y2')) {
            chartData.datasets.push({ data: Array(12).fill(0)?.map(() => Math.random() * 50), type: 'line' as any, order: 0, yAxisID: 'y2' })
        }

        newOptions = { ...newOptions,
            scales: { y: { display: false }, y2: { display: false } },
            plugins: {

                legend: { display: false },
                datalabels: { display: false }
            }
        }
    }

    ChartsHelper.applyColors(chartData.datasets as any, isLoading)
    ChartsHelper.applyMode(newOptions)

    return (
        <div style={{ flex: 1 }}>
            <Bar
                data={chartData}
                ref={chartRef}
                width='100%'
                height='100%'
                options={{
                    ...newOptions,
                    onHover: (event, activeElements) => handleChartHover(event.native, activeElements)
                }}
                onClick={handleChartClick}
            />
        </div>
    )
}

export default BarChart
const BarChartDefaultOptions: ChartOptions<'bar'> = {
    maintainAspectRatio: false,
    elements: {
        line: { tension: 0.5 }
    },
    plugins: {
        tooltip: {
            callbacks: {}
        },
        datalabels: { display: false }
    },
    scales: {
        x: {
            grid: { display: false },
            ticks: {}
        },
        y: {
            grid: { color: 'rgba(166,166,166, 0.35)' },
            ticks: {
                font: { size: 11 },
                callback(tickValue) {
                    const formatter = Intl.NumberFormat('en', { notation: 'compact' })
                    return formatter.format(tickValue as number)
                }
            }
        }
    },
    datasets: {
        bar: {
            borderRadius: 4,
            borderSkipped: false
        }
    }
}
export const GetBarChartDefaultOptions = () => _.cloneDeep(BarChartDefaultOptions)
